'use client';

import { useEffect, useRef } from 'react';

/**
 * Adds an Escape key listener that calls `onClose` when the modal is open.
 * Also locks body scroll while the modal is open (restores on cleanup),
 * traps focus within the closest `[role="dialog"]` ancestor, and restores
 * focus to the previously-focused element on close.
 *
 * Usage:
 *   const close = () => setOpen(false);
 *   useEscapeClose(open, close);
 *   useEscapeClose(open, close, { lockScroll: false }); // inline (non-overlay) panels
 */
export function useEscapeClose(open: boolean, onClose: () => void, opts?: { lockScroll?: boolean }) {
  const previousActiveRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    previousActiveRef.current = (document.activeElement as HTMLElement) ?? null;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const dialog = document.querySelector('[role="dialog"]');
      if (!dialog) return;
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || !dialog.contains(document.activeElement)) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last || !dialog.contains(document.activeElement)) {
          first.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', onKey);
    const lockScroll = opts?.lockScroll !== false;
    const prevOverflow = document.body.style.overflow;
    if (lockScroll) document.body.style.overflow = 'hidden';

    // Move focus into the dialog on open (if a [role=dialog] exists).
    const dialog = document.querySelector('[role="dialog"]');
    if (dialog) {
      const target = dialog.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      target?.focus();
    }

    return () => {
      document.removeEventListener('keydown', onKey);
      if (lockScroll) document.body.style.overflow = prevOverflow;
      previousActiveRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
